import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { toast, ToastContainer, ToastOptions } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Notification types
export type NotificationType = 'info' | 'success' | 'warning' | 'error';

// Individual notification interface
export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  persistent?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

// Notification options
export interface NotificationOptions {
  title?: string;
  persistent?: boolean;
  autoClose?: number | false;
  position?: ToastOptions['position'];
  action?: {
    label: string;
    onClick: () => void;
  };
}

// Context interface
interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  showNotification: (type: NotificationType, message: string, options?: NotificationOptions) => string;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  // Toast shortcuts
  showSuccess: (message: string, options?: NotificationOptions) => string;
  showError: (message: string, options?: NotificationOptions) => string;
  showWarning: (message: string, options?: NotificationOptions) => string;
  showInfo: (message: string, options?: NotificationOptions) => string;
}

// Create context
const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Provider props
interface NotificationProviderProps {
  children: ReactNode;
  maxNotifications?: number;
}

// Provider component
export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  maxNotifications = 100
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Generate unique ID
  const generateId = useCallback(() => {
    return `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.read).length;

  // Main notification function
  const showNotification = useCallback((
    type: NotificationType,
    message: string,
    options: NotificationOptions = {}
  ): string => {
    const id = generateId();
    const {
      title,
      persistent = false,
      autoClose = 5000,
      position = 'top-right',
      action
    } = options;

    // Create notification object
    const notification: Notification = {
      id,
      type,
      title: title || type.charAt(0).toUpperCase() + type.slice(1),
      message,
      timestamp: new Date(),
      read: false,
      persistent,
      action
    };

    // Add to notifications list
    setNotifications(prev => {
      const newNotifications = [notification, ...prev];
      // Limit to max notifications
      return newNotifications.slice(0, maxNotifications);
    });

    // Show toast
    const toastOptions: ToastOptions = {
      position,
      autoClose: persistent ? false : autoClose,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      toastId: id,
    };

    // Custom toast content with action
    const ToastContent = () => (
      <div className="flex flex-col">
        {title && title !== type.charAt(0).toUpperCase() + type.slice(1) && (
          <div className="font-semibold text-sm mb-1">{title}</div>
        )}
        <div className="text-sm">{message}</div>
        {action && (
          <button
            onClick={action.onClick}
            className="mt-2 text-xs bg-white bg-opacity-20 hover:bg-opacity-30 px-2 py-1 rounded text-left transition-colors"
          >
            {action.label}
          </button>
        )}
      </div>
    );

    // Show appropriate toast type
    switch (type) {
      case 'success':
        toast.success(<ToastContent />, toastOptions);
        break;
      case 'error':
        toast.error(<ToastContent />, toastOptions);
        break;
      case 'warning':
        toast.warning(<ToastContent />, toastOptions);
        break;
      case 'info':
      default:
        toast.info(<ToastContent />, toastOptions);
        break;
    }

    return id;
  }, [generateId, maxNotifications]);

  // Shortcut methods
  const showSuccess = useCallback((message: string, options?: NotificationOptions) =>
    showNotification('success', message, options), [showNotification]);

  const showError = useCallback((message: string, options?: NotificationOptions) =>
    showNotification('error', message, options), [showNotification]);

  const showWarning = useCallback((message: string, options?: NotificationOptions) =>
    showNotification('warning', message, options), [showNotification]);

  const showInfo = useCallback((message: string, options?: NotificationOptions) =>
    showNotification('info', message, options), [showNotification]);

  // Mark notification as read
  const markAsRead = useCallback((id: string) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === id
          ? { ...notification, read: true }
          : notification
      )
    );
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, read: true }))
    );
  }, []);

  // Remove notification
  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
    toast.dismiss(id);
  }, []);

  // Clear all notifications
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
    toast.dismiss();
  }, []);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    showNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        className="!z-[9999]"
        toastClassName="!rounded-lg !shadow-lg"
        bodyClassName="!p-0"
        progressClassName="!bg-opacity-30"
      />
    </NotificationContext.Provider>
  );
};

// Hook to use notifications
export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Export types
export type { NotificationContextType }; 